import type { AssessmentQuestion, BrewingScenario } from './types';

// Coffee brewing scenarios for assessment
export const brewingScenarios: BrewingScenario[] = [
  {
    id: 'pour_over_morning',
    title: 'Morning Pour Over',
    description:
      'You want to make a bright, clean cup of coffee to start your day',
    context:
      "It's 7 AM and you have 10 minutes before work. You have a V60, paper filters, and freshly ground medium-light roast beans.",
    equipment: ['V60', 'Paper filters', 'Gooseneck kettle', 'Scale'],
    difficulty: 3,
    timeRequired: 8,
  },
  {
    id: 'espresso_afternoon',
    title: 'Afternoon Espresso',
    description:
      'You want a rich, concentrated shot for an afternoon pick-me-up',
    context:
      'You have an espresso machine, grinder, and various coffee beans. You want to pull a perfect shot.',
    equipment: ['Espresso machine', 'Grinder', 'Tamper', 'Scale'],
    difficulty: 7,
    timeRequired: 5,
  },
  {
    id: 'french_press_weekend',
    title: 'Weekend French Press',
    description:
      'Relaxed weekend morning with time to enjoy the brewing process',
    context:
      'Saturday morning, no rush. You want to make coffee for yourself and a friend using a French press.',
    equipment: ['French press', 'Burr grinder', 'Kettle', 'Timer'],
    difficulty: 2,
    timeRequired: 6,
  },
];

// Assessment questions with coffee brewing contexts
export const assessmentQuestions: AssessmentQuestion[] = [
  // Experience Level Questions
  {
    id: 'experience_level',
    type: 'multiple_choice',
    question: 'How would you describe your coffee brewing experience?',
    description: 'This helps us understand your starting point',
    category: 'experience',
    weight: 1.0,
    aiContext:
      'Determines baseline skill level and appropriate content difficulty',
    options: [
      {
        id: 'beginner',
        text: 'Complete beginner',
        value: 'beginner',
        skillLevel: 'BEGINNER' as any,
        description: "I'm just starting my coffee journey",
        icon: '🌱',
      },
      {
        id: 'some_experience',
        text: 'Some experience',
        value: 'intermediate',
        skillLevel: 'INTERMEDIATE' as any,
        description: 'I can make basic coffee but want to improve',
        icon: '☕',
      },
      {
        id: 'experienced',
        text: 'Experienced home brewer',
        value: 'advanced',
        skillLevel: 'ADVANCED' as any,
        description: 'I know various brewing methods and techniques',
        icon: '🎯',
      },
      {
        id: 'expert',
        text: 'Coffee expert/professional',
        value: 'expert',
        skillLevel: 'ADVANCED' as any, // Using ADVANCED for expert level
        description: 'I have professional experience or deep expertise',
        icon: '👨‍🍳',
      },
    ],
  },
  {
    id: 'brewing_methods',
    type: 'multiple_choice',
    question: 'Which brewing methods have you used before?',
    description: 'Select all that apply',
    category: 'methods',
    weight: 0.8,
    aiContext:
      'Identifies familiar brewing methods for personalized recommendations',
    options: [
      {
        id: 'drip_coffee',
        text: 'Drip coffee maker',
        value: 'drip',
        description: 'Automatic drip machines',
        icon: '☕',
      },
      {
        id: 'french_press',
        text: 'French Press',
        value: 'french_press',
        description: 'Immersion brewing method',
        icon: '🫖',
      },
      {
        id: 'pour_over',
        text: 'Pour Over (V60, Chemex, etc.)',
        value: 'pour_over',
        description: 'Manual pour over methods',
        icon: '⏳',
      },
      {
        id: 'espresso',
        text: 'Espresso Machine',
        value: 'espresso',
        description: 'Pressure-based brewing',
        icon: '☕',
      },
      {
        id: 'aeropress',
        text: 'AeroPress',
        value: 'aeropress',
        description: 'Pressure and immersion hybrid',
        icon: '🔄',
      },
      {
        id: 'cold_brew',
        text: 'Cold Brew',
        value: 'cold_brew',
        description: 'Cold water extraction',
        icon: '🧊',
      },
    ],
  },
  {
    id: 'equipment_ownership',
    type: 'multiple_choice',
    question: 'What coffee equipment do you currently own?',
    description: 'This helps us recommend appropriate recipes and techniques',
    category: 'equipment',
    weight: 0.9,
    aiContext: 'Determines available equipment for recipe recommendations',
    options: [
      {
        id: 'basic_setup',
        text: 'Basic setup (coffee maker, basic grinder)',
        value: 'basic',
        description: 'Standard home coffee equipment',
      },
      {
        id: 'intermediate_setup',
        text: 'Intermediate setup (pour over, burr grinder)',
        value: 'intermediate',
        description: 'Manual brewing equipment',
      },
      {
        id: 'advanced_setup',
        text: 'Advanced setup (espresso machine, precision tools)',
        value: 'advanced',
        description: 'Professional-grade equipment',
      },
      {
        id: 'minimal_setup',
        text: 'Minimal/no equipment',
        value: 'minimal',
        description: 'Looking to build my coffee setup',
      },
    ],
  },
  {
    id: 'time_availability',
    type: 'multiple_choice',
    question: 'How much time do you typically have for coffee brewing?',
    description: 'This helps us suggest appropriate brewing methods',
    category: 'preferences',
    weight: 0.7,
    aiContext: 'Matches brewing methods to available time constraints',
    options: [
      {
        id: 'quick_5min',
        text: '5 minutes or less',
        value: 5,
        description: 'Quick and convenient brewing',
      },
      {
        id: 'moderate_10min',
        text: '5-10 minutes',
        value: 10,
        description: 'Some time for manual brewing',
      },
      {
        id: 'relaxed_15min',
        text: '10-15 minutes',
        value: 15,
        description: 'Enjoy the brewing process',
      },
      {
        id: 'leisurely_20min',
        text: '15+ minutes',
        value: 20,
        description: 'Love taking time with coffee',
      },
    ],
  },
  {
    id: 'learning_goals',
    type: 'multiple_choice',
    question: 'What are your main coffee learning goals?',
    description: 'Select your top priorities',
    category: 'goals',
    weight: 0.9,
    aiContext: 'Personalizes learning journey based on specific interests',
    options: [
      {
        id: 'better_taste',
        text: 'Make better tasting coffee',
        value: 'taste_improvement',
        description: 'Focus on flavor and technique',
      },
      {
        id: 'learn_methods',
        text: 'Learn new brewing methods',
        value: 'method_exploration',
        description: 'Explore different brewing techniques',
      },
      {
        id: 'understand_science',
        text: 'Understand coffee science',
        value: 'coffee_science',
        description: 'Learn the why behind brewing',
      },
      {
        id: 'equipment_mastery',
        text: 'Master my equipment',
        value: 'equipment_mastery',
        description: 'Get the most from my gear',
      },
      {
        id: 'consistency',
        text: 'Brew consistently great coffee',
        value: 'consistency',
        description: 'Reliable, repeatable results',
      },
    ],
  },
  // Scenario-based questions
  {
    id: 'pour_over_scenario',
    type: 'scenario',
    question: 'How would you approach this brewing scenario?',
    description: 'Choose the approach that feels most natural to you',
    category: 'application',
    weight: 0.8,
    aiContext: 'Assesses practical application of coffee knowledge',
    scenario: brewingScenarios[0],
    options: [
      {
        id: 'follow_recipe',
        text: 'Follow a specific recipe exactly',
        value: 'recipe_focused',
        description: 'Prefer structured, step-by-step guidance',
      },
      {
        id: 'adjust_by_taste',
        text: 'Start with a recipe, adjust by taste',
        value: 'adaptive',
        description: 'Use recipes as starting points',
      },
      {
        id: 'experiment_freely',
        text: 'Experiment based on experience',
        value: 'experimental',
        description: 'Trust my instincts and experience',
      },
      {
        id: 'need_guidance',
        text: "I'd need step-by-step guidance",
        value: 'guided',
        description: 'Prefer detailed instructions',
      },
    ],
  },
  {
    id: 'taste_preferences',
    type: 'preference',
    question: 'What flavor profiles do you enjoy most?',
    description:
      'This helps us recommend suitable coffee beans and brewing methods',
    category: 'taste',
    weight: 0.6,
    aiContext: 'Matches content to taste preferences and bean recommendations',
    options: [
      {
        id: 'bright_acidic',
        text: 'Bright and acidic',
        value: 'bright',
        description: 'Light roasts, fruity notes',
      },
      {
        id: 'balanced_smooth',
        text: 'Balanced and smooth',
        value: 'balanced',
        description: 'Medium roasts, well-rounded',
      },
      {
        id: 'rich_bold',
        text: 'Rich and bold',
        value: 'bold',
        description: 'Dark roasts, strong flavors',
      },
      {
        id: 'sweet_chocolatey',
        text: 'Sweet and chocolatey',
        value: 'sweet',
        description: 'Caramel, chocolate notes',
      },
      {
        id: 'not_sure',
        text: "I'm not sure yet",
        value: 'exploring',
        description: 'Still discovering preferences',
      },
    ],
  },
  {
    id: 'learning_style',
    type: 'preference',
    question: 'How do you prefer to learn new skills?',
    description: 'This helps us personalize your learning experience',
    category: 'learning',
    weight: 0.7,
    aiContext: 'Customizes content delivery and learning approach',
    options: [
      {
        id: 'visual_learner',
        text: 'Visual demonstrations and videos',
        value: 'visual',
        description: 'Learn by watching and seeing',
      },
      {
        id: 'hands_on',
        text: 'Hands-on practice and experimentation',
        value: 'kinesthetic',
        description: 'Learn by doing and trying',
      },
      {
        id: 'step_by_step',
        text: 'Detailed written instructions',
        value: 'reading',
        description: 'Learn through detailed guides',
      },
      {
        id: 'interactive',
        text: 'Interactive lessons and quizzes',
        value: 'interactive',
        description: 'Learn through engagement',
      },
    ],
  },
];
